import { useMemo, useState } from 'react';
import { FormControlLabel, Menu, MenuItem } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { Checkbox } from 'src/components/Checkbox';
import { ChevronIcon } from 'src/components/ChevronIcon';
import { CUSTOM_BUTTON_VARIANT, CustomButton } from 'src/components/CustomButton';
import { TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { getResourceTagLabel } from 'src/data/performance';
import { ROTATE_DIRECTION } from 'src/data/ui';

import { useStyles } from './useStyles';

const MENU_PROPS = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
};

export const FilterResourceByTag = ({ className, classes, value, onChange, tags, disabled }) => {
  const styles = useStyles({ classes });

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChange = (event) => {
    const { name, checked } = event.target;
    if (checked) {
      value.push(name);
    } else {
      const index = value.indexOf(name);
      value.splice(index, 1);
    }

    const alignedValue = value.length ? [...value] : [];

    onChange(alignedValue);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const allItemsSelected = useMemo(() => tags.every((c) => value.includes(c)), [tags, value]);

  const handleSelectAll = () => {
    if (allItemsSelected) {
      onChange([]);
    } else {
      onChange(tags);
    }
  };

  return (
    <>
      <CustomButton
        className={cx(styles.root, className, { [styles.focused]: open || value.length })}
        onClick={handleClick}
        variant={CUSTOM_BUTTON_VARIANT.outlined}
        endIcon={
          <ChevronIcon
            className={styles.chevronIcon}
            direction={open ? ROTATE_DIRECTION.up : ROTATE_DIRECTION.down}
          />
        }
        data-testid="FilterResourceByTag"
        disabled={disabled}
      >
        <TruncatedTypography
          className={styles.label}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          {value?.length ? value.map(getResourceTagLabel).join(', ') : 'Tags'}
        </TruncatedTypography>
      </CustomButton>
      <Menu
        classes={{
          paper: styles.menuPaper,
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        variant="menu"
        {...MENU_PROPS}
      >
        <div className={styles.filterCampaignsItems}>
          <MenuItem className={cx(styles.menuItem)}>
            <FormControlLabel
              control={
                <Checkbox
                  className={styles.checkbox}
                  checked={allItemsSelected}
                  onChange={handleSelectAll}
                  name="All"
                  color="primary"
                />
              }
              label={<TruncatedTypography className={styles.optionLabel}>All</TruncatedTypography>}
              disabled={disabled}
            />
          </MenuItem>
          {tags.map((tag) => (
            <MenuItem
              key={tag}
              className={styles.menuItem}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    className={styles.checkbox}
                    checked={value.includes(tag)}
                    onChange={handleChange}
                    name={tag}
                    color="primary"
                  />
                }
                label={
                  <TruncatedTypography className={styles.optionLabel}>
                    {getResourceTagLabel(tag)}
                  </TruncatedTypography>
                }
                disabled={disabled}
              />
            </MenuItem>
          ))}
        </div>
      </Menu>
    </>
  );
};

FilterResourceByTag.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape(),
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
};

FilterResourceByTag.defaultProps = {
  className: null,
  classes: null,
  value: [],
  tags: [],
  disabled: false,
};
